import { Group } from '@/types/core/Group';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  List,
  ListItem,
  Show,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import {
  PiGlobeSimpleThin,
  PiHashThin,
  PiLockThin,
  PiPlusThin,
} from 'react-icons/pi';
import React, { useMemo } from 'react';
import NextLink from 'next/link';

interface GroupsHomeNavbarProps {
  userGroups: Group[];
  thematicsGroups: Group[];
  countryGroups: Group[];
  selectedGroup?: Group | null;
  onClickCreateGroup: () => void;
  onClickGroup?: (group: Group) => void;
}

function NavbarItem({
  title,
  groups,
  url,
  selectedGroup,
  onClickGroup,
  linksToDiscussions = false
}: {
  title: string;
  groups?: Group[];
  url?: string;
  selectedGroup?: Group | null;
  onClickGroup?: (group: Group) => void;
  linksToDiscussions?: boolean
}) {
  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <AccordionItem border="0">
        <AccordionButton
          px="0"
          _hover={{
            background: 'transparent',
          }}
        >
          <Flex w="100%" align="center" justify="space-between">
            <Flex align="center" gap="1rem">
              {!selectedGroup && !groups && (
                <Box w="1.875rem" h="0.625rem" bg="tournesol.100" />
              )}
              <Heading
                variant="h4"
                textAlign="left"
                wordBreak="break-word"
                color={!selectedGroup && !groups ? 'brand.100' : 'black'}
              >
                <NextLink href={'/communautes/groupes'} passHref>
                  <Link>{title}</Link>
                </NextLink>
              </Heading>
            </Flex>
            {groups && groups.length > 0 && <AccordionIcon />}
          </Flex>
        </AccordionButton>
        {groups && groups.length > 0 && (
          <AccordionPanel pb="0" px="0">
            <List as={Flex} direction="column" gap="0.5rem">
              {groups.map(group => (
                <ListItem key={group.id}>
                  <Flex align="center" gap="0.5rem">
                    {selectedGroup?.id === group.id && (
                      <Box w="1.875rem" h="0.625rem" bg="tournesol.100" />
                    )}
                    <Icon
                      as={group.private ? PiLockThin : PiHashThin}
                      color={selectedGroup?.id === group.id && 'brand.100'}
                    />
                    <NextLink
                      href={{
                        pathname: linksToDiscussions
                          ? '/groupe/discussions'
                          : '/groupe/detail',
                        query: { group: group.id, slug: group.slug },
                      }}
                      as={`/groupe/${group.slug}/${group.id}${linksToDiscussions ? '/discussions' : ''}`}
                      passHref
                    >
                      <Link
                        onClick={() => onClickGroup && onClickGroup(group)}
                        wordBreak="break-word"
                        color={selectedGroup?.id === group.id && 'brand.100'}
                        fontWeight={selectedGroup?.id === group.id && '600'}
                      >
                        <>
                          {group.name}
                          {group.country == null && (
                            <>
                              <Icon as={PiGlobeSimpleThin} marginLeft=".5ch" />
                              <VisuallyHidden>(Groupe monde)</VisuallyHidden>
                            </>
                          )}
                        </>
                      </Link>
                    </NextLink>
                  </Flex>
                </ListItem>
              ))}
            </List>
          </AccordionPanel>
        )}
      </AccordionItem>
    </Accordion>
  );
}

export default function GroupsHomeNavbar({
  userGroups,
  thematicsGroups,
  countryGroups,
  selectedGroup,
  onClickCreateGroup,
  onClickGroup,
}: GroupsHomeNavbarProps) {
  return (
    <Flex direction="column" gap="1rem">
      <NavbarItem
        title={'Tous mes groupes'}
        selectedGroup={selectedGroup}
        onClickGroup={onClickGroup}
      />
      <NavbarItem
        title={'Mes groupes'}
        groups={userGroups}
        selectedGroup={selectedGroup}
        onClickGroup={onClickGroup}
        linksToDiscussions
      />
      <Divider sx={{ borderColor: 'brand.100' }} />
      <Text color="brand.100" fontWeight="bold">
        Rejoindre d’autres groupes&nbsp;:
      </Text>
      <NavbarItem
        title={'Groupes monde'}
        groups={thematicsGroups}
        selectedGroup={selectedGroup}
        onClickGroup={onClickGroup}
      />
      <NavbarItem
        title={'Groupes de mon pays'}
        groups={countryGroups}
        selectedGroup={selectedGroup}
        onClickGroup={onClickGroup}
      />

      <Flex direction="column" align="center" w="100%" gap="1.625rem">
        <Divider sx={{ borderColor: 'brand.100' }} />
        {/*<Button*/}
        {/*  variant="sunflowerOutline"*/}
        {/*  m={['0.5rem auto', '1rem auto 0.625rem auto']}*/}
        {/*  w="fit-content"*/}
        {/*  size="sm"*/}
        {/*>*/}
        {/*  <Show breakpoint="(min-width: 834px)">*/}
        {/*    <Icon as={PiFoldersThin} boxSize={5} mr="0.625rem" />*/}
        {/*  </Show>*/}
        {/*  Découvrir d'autres groupes*/}
        {/*</Button>*/}
        <Show breakpoint="(min-width: 834px)">
          <Button
            variant="sunflower"
            size="sm"
            w="fit-content"
            onClick={onClickCreateGroup}
          >
            <Icon as={PiPlusThin} boxSize={5} mr="0.625rem" />
            Créer un groupe
          </Button>
        </Show>
      </Flex>
    </Flex>
  );
}
